/**
 * AdminRoute Component
 * 
 * Protected route wrapper for admin panel pages
 * Ensures only authenticated admin users can access admin routes
 * 
 * Security Features:
 * - Checks localStorage for admin token
 * - Verifies token with backend
 * - Redirects non-admin users to access denied page
 * - Shows loading state during verification
 */

import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

// Use environment variable for API URL, fallback to localhost for local development
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

const AdminRoute = ({ children }) => {
  const [isVerifying, setIsVerifying] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const location = useLocation();

  useEffect(() => {
    verifyAdminAccess();
  }, []);

  /**
   * Verify admin access by checking:
   * 1. Token exists in localStorage
   * 2. Token is valid (not expired)
   * 3. User has admin role
   */
  const verifyAdminAccess = async () => {
    try {
      // Get admin token from localStorage
      const adminToken = localStorage.getItem('adminToken');
      
      if (!adminToken) {
        setIsAdmin(false);
        setIsVerifying(false);
        return;
      }

      // Verify token with backend by making a test request
      const response = await fetch(`${API_URL}/admin/dashboard`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        // Token is valid and user is admin
        setIsAdmin(true);
      } else {
        // Token invalid or user not admin
        setIsAdmin(false);
        // Clear invalid token
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminUser');
      }
    } catch (error) {
      console.error('Error verifying admin access:', error);
      setIsAdmin(false);
    } finally {
      setIsVerifying(false);
    }
  };

  // Show loading state while verifying
  if (isVerifying) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        fontSize: '1.2rem',
        color: '#666'
      }}>
        Verifying admin access...
      </div>
    );
  }

  // Redirect to access denied if not admin
  if (!isAdmin) {
    return <Navigate to="/access-denied" state={{ from: location }} replace />;
  }

  // Render protected admin content
  return children;
};

export default AdminRoute;

